import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Flex,
  FormMessage,
  Input,
  Label,
  Spinner,
  Select,
  theme,
  Text,
  Link,
} from '@xometry/xometry_loft';
import { Formik } from 'formik';
import * as yup from 'yup';
import Account from '../../../../services/account';
import {
  street1,
  street2,
  city,
  state,
  zip,
} from '../../../../utils/validations';
import { poBoxPattern, states } from '../../../../utils/constants';

const XACRegisterForm = () => {
  const [formError, setFormError] = useState('');
  const [validBusinessAddressForShipping, setValidBusinessAddressForShipping] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stripeUserData, setStripeUserData] = useState<any>(null);
  const account = new Account();

  const stateOptions: any[] = [];
  const keys = Object.keys(states);
  const [initialValues, setInitialValues] = useState({
    useBusinessAddress: true,
    cardHolderName: '',

    // Optional Shipping Address
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    entityType: null,
    termsAccepted: false,
  });

  useEffect(() => {
    const fetchInitialValues = () => {
      account
        .getStripeUser()
        .then((stripeApiData) => {
          setStripeUserData(stripeApiData);

          let address = {
            line1: '',
            line2: '',
            city: '',
            state: '',
            postal_code: '',
          };
          // If they have the legal_entity object and address, populate the address on the form with it
          if (stripeApiData?.legal_entity?.address) {
            address = stripeApiData.legal_entity.address;
          }

          // Check if the business address is using a PO box in line1.
          // If it is, they HAVE to give us a different shipping address
          // Stripe will not ship a card to a PO box.
          let showShippingByDefault = poBoxPattern.test(address.line1);
          // Check the second line too if it exists
          if (address.line2) {
            showShippingByDefault =
              showShippingByDefault || poBoxPattern.test(address.line2);
          }

          // Check to see that an express account has an address and if not,
          // show the boxes to add the shipping data
          if (!stripeApiData?.legal_entity) {
            showShippingByDefault = true;
          }

          // if line1, city, state or postal code is empty string
          // we want to show the shipping address form
          if (
            !address.line1 ||
            !address.city ||
            !address.postal_code ||
            !address.state
          ) {
            showShippingByDefault = true;
          }
          // If showShippingByDefault is true, the stripeApiData.legal_entity.address is not valid for Shipping
          setValidBusinessAddressForShipping(!showShippingByDefault);

          // Set the initial values for the form based on what comes back, if anything, from the API
          setInitialValues({
            ...initialValues,
            useBusinessAddress: !showShippingByDefault,
            // Optional Shipping Address
            street1: address.line1,
            street2: address.line2 ?? '',
            city: address.city,
            state: address.state,
            zipcode: address.postal_code,
            entityType: stripeApiData.legal_entity?.type,
          });
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setIsLoading(false);
        });
    };

    fetchInitialValues();
  }, []);

  const schema = yup.object().shape({
    useBusinessAddress: yup.boolean(),
    cardHolderName: yup
      .string()
      .max(24, 'Cardholder Name Cannot Exceed 24 Characters')
      .required('Cardholder Name is Required'),
    street1: street1.when('useBusinessAddress', {
      is: (useBusinessAddress: boolean) => !useBusinessAddress,
      then: street1
        .test(
          'is-po-box',
          'Shipping Address cannot be a PO Box',
          (val) => !poBoxPattern.test(val ?? '')
        )
        .required('Street Address is Required'),
      otherwise: street1,
    }),
    street2: street2
      .test(
        'is-po-box',
        'Shipping Address cannot be a PO Box',
        (val) => !poBoxPattern.test(val ?? '')
      )
      .nullable(),
    city: city.when('useBusinessAddress', {
      is: (useBusinessAddress: boolean) => !useBusinessAddress,
      then: city.required('City is Required'),
      otherwise: city,
    }),
    state: state.when('useBusinessAddress', {
      is: (useBusinessAddress: boolean) => !useBusinessAddress,
      then: state.required('State is Required'),
      otherwise: state,
    }),
    zipcode: zip.when('useBusinessAddress', {
      is: (useBusinessAddress: boolean) => !useBusinessAddress,
      then: zip.required('Zip Code is Required'),
      otherwise: zip,
    }),
    termsAccepted: yup.boolean().when('entityType', {
      is: (entityType: string) => entityType === 'individual',
      then: yup
        .boolean()
        .oneOf(
          [true],
          'You must agree to the Stripe Authorized User Terms and E-SIGN Disclosure.'
        ),
    }),
  });

  // eslint-disable-next-line
  for (const key of keys) {
    stateOptions.push(
      <option key={key} value={states[key]}>
        {states[key]}
      </option>
    );
  }

  if (isLoading) return <Spinner />;

  return (
    <>
      {!isLoading && (
        <Box textAlign="left">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              // eslint-disable-next-line prefer-const
              let payload: any = {
                cardholderName: values.cardHolderName,
              };

              if (values.entityType === 'individual') {
                payload.termsAccepted = values.termsAccepted;
              }

              // If the user is not using the Business address for shipping,
              // create it from the form values
              if (!values.useBusinessAddress) {
                payload.shippingAddress = {
                  city: values.city,
                  country: 'US',
                  line1: values.street1,
                  postal_code: values.zipcode,
                  state: values.state,
                };
                // If they provide a line2 on shipping, send it.
                // If you send it when blank, the stripe API complains
                if (values.street2) {
                  payload.shippingAddress.line2 = values.street2;
                }
              } else {
                // When user has a valid business address and selects Shipping as Business
                const stripeUserAddress: {
                  city: string;
                  state: string;
                  line1: string;
                  line2: string;
                  // eslint-disable-next-line camelcase
                  postal_code: string;
                } = stripeUserData.legal_entity.address;

                payload.shippingAddress = {
                  city: stripeUserAddress.city,
                  country: 'US',
                  line1: stripeUserAddress.line1,
                  postal_code: stripeUserAddress.postal_code,
                  state: stripeUserAddress.state,
                };
                // If line2 is included on legal_entity_address on stripeUserData, send it.
                // If you send it when blank, the stripe API complains
                if (stripeUserAddress?.line2) {
                  payload.shippingAddress.line2 = stripeUserAddress.line2;
                }
              }

              account
                .createCard(payload)
                .then(() => {
                  setSubmitting(false);
                  window.location.href = '/account/supplier/onboarding';
                })
                .catch(() => {
                  setSubmitting(false);
                  setFormError(
                    'There was an error creating your card. Please try again.'
                  );
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              /* and other goodies */
            }) => {
              // Business address validation, only used if a stripe express user comes through.
              const invalidStreetName = Boolean(
                (errors.street1 && touched.street1) ||
                  (errors.street2 && touched.street2)
              );
              const invalidCity = Boolean(errors.city && touched.city);
              const invalidState = Boolean(errors.state && touched.state);
              const invalidZipCode = Boolean(errors.zipcode && touched.zipcode);
              return (
                <form onSubmit={handleSubmit}>
                  <Flex flexDirection="column" justifyContent="center">
                    <Label htmlFor="cardHolderName">Cardholder Name</Label>
                    <Input
                      className="pii-encrypt"
                      id="cardHolderName"
                      type="text"
                      name="cardHolderName"
                      height="52px"
                      padding="7px 8px"
                      scale="xlarge"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.cardHolderName}
                      invalid={Boolean(
                        errors.cardHolderName && touched.cardHolderName
                      )}
                    />
                    {errors.cardHolderName && touched.cardHolderName && (
                      <FormMessage
                        invalid={Boolean(
                          errors.cardHolderName && touched.cardHolderName
                        )}
                      >
                        {errors.cardHolderName}
                      </FormMessage>
                    )}
                  </Flex>
                  {validBusinessAddressForShipping && (
                    <Flex margin="20px 0px" alignItems="center">
                      <Checkbox
                        checked={values.useBusinessAddress}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        id="useBusinessAddress"
                        data-testid="useBusinessAddressCheckbox"
                        name="useBusinessAddress"
                        enterKeyHint="next"
                      />
                      <Text paddingLeft={theme.space[1]}>
                        Shipping Address is the same as Business Address
                      </Text>
                    </Flex>
                  )}
                  {(!values.useBusinessAddress ||
                    !validBusinessAddressForShipping) && (
                    <Flex
                      flexDirection="column"
                      justifyContent="center"
                      marginTop="16px"
                    >
                      <Label htmlFor="street1">Shipping Address</Label>
                      <Input
                        className="pii-encrypt"
                        placeholder="Line 1"
                        id="street1"
                        type="text"
                        name="street1"
                        height="52px"
                        padding="7px 8px"
                        scale="xlarge"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.street1}
                        marginBottom="6px"
                        invalid={invalidStreetName}
                      />
                      <Input
                        className="pii-encrypt"
                        placeholder="Line 2"
                        type="street2"
                        name="street2"
                        height="52px"
                        padding="7px 8px"
                        scale="xlarge"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.street2}
                        invalid={invalidStreetName}
                      />
                      {invalidStreetName && (
                        <FormMessage invalid={invalidStreetName}>
                          {errors.street2 || errors.street1}
                        </FormMessage>
                      )}
                      <FormMessage marginTop="8px">
                        Shipping Address cannot be a PO Box
                      </FormMessage>
                      <Label htmlFor="city" marginTop="12px">
                        City
                      </Label>
                      <Input
                        className="pii-encrypt"
                        id="city"
                        name="city"
                        height="52px"
                        padding="7px 8px"
                        scale="xlarge"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={invalidCity}
                      />
                      {invalidCity && (
                        <FormMessage invalid={invalidCity}>
                          {errors.city}
                        </FormMessage>
                      )}
                      <Flex
                        marginTop={['0px', '20px']}
                        marginBottom="38px"
                        flexDirection={['column', 'row']}
                      >
                        <Flex
                          width={[1, 1 / 2]}
                          marginTop={['24px', '0px']}
                          flexDirection="column"
                        >
                          <Label htmlFor="state">State</Label>
                          <Select
                            className="pii-encrypt"
                            id="state"
                            name="state"
                            width="100%"
                            height="52px"
                            padding="7px 8px"
                            scale="xlarge"
                            backgroundColor="white"
                            marginRight={['0px', '6px']}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={invalidState}
                            value={values.state}
                          >
                            <option value="">Select</option>
                            {stateOptions}
                          </Select>
                          {invalidState && (
                            <FormMessage invalid={invalidState}>
                              {errors.state}
                            </FormMessage>
                          )}
                        </Flex>
                        <Flex
                          width={[1, 1 / 2]}
                          marginTop={['24px', '0px']}
                          flexDirection="column"
                        >
                          <Label htmlFor="zipcode">ZIP</Label>
                          <Input
                            className="pii-encrypt"
                            type="zipcode"
                            id="zipcode"
                            name="zipcode"
                            height="52px"
                            padding="7px 8px"
                            scale="xlarge"
                            marginLeft={['0px', '6px']}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.zipcode}
                            invalid={invalidZipCode}
                          />
                          {invalidZipCode && (
                            <FormMessage invalid={invalidZipCode}>
                              {errors.zipcode}
                            </FormMessage>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  )}
                  {/* Terms Accepted Checkbox for Individual Entities */}
                  {initialValues.entityType === 'individual' && (
                    <>
                      <Checkbox
                        checked={values.termsAccepted}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        id="termsAccepted"
                        name="termsAccepted"
                        type="checkbox"
                        data-testid="termsAcceptedCheckbox"
                        enterKeyHint="enter"
                        invalid={Boolean(
                          errors.termsAccepted && touched.termsAccepted
                        )}
                      />
                      <Text paddingLeft={theme.space[1]}>
                        I agree to the{' '}
                        <Link
                          href="https://stripe.com/legal/issuing/celtic-authorized-user-terms"
                          target="_blank"
                        >
                          Stripe Authorized User Terms
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://stripe.com/legal/e-sign-disclosure"
                          target="_blank"
                        >
                          E-SIGN Disclosure
                        </Link>
                        .
                      </Text>
                      {errors.termsAccepted && touched.termsAccepted && (
                        <FormMessage
                          invalid={Boolean(
                            errors.termsAccepted && touched.termsAccepted
                          )}
                        >
                          {errors.termsAccepted}
                        </FormMessage>
                      )}
                    </>
                  )}
                  <Box textAlign="center" marginTop="36px">
                    {formError && (
                      <Alert kind="danger" marginY={theme.space[5]} isOpen>
                        {formError}
                      </Alert>
                    )}
                    {isSubmitting && (
                      <Spinner margin="40px auto 0px auto" scale="panel" />
                    )}
                    {!isSubmitting && (
                      <Flex>
                        <Button
                          width="fill-available"
                          kind="text-blue"
                          scale="large"
                          type="button"
                          marginRight={theme.space[3]}
                          onClick={() =>
                            window.location.assign(
                              '/account/supplier/onboarding'
                            )
                          }
                        >
                          Skip This Step
                        </Button>
                        <Button
                          width="fill-available"
                          type="submit"
                          scale="large"
                          kind="primary"
                        >
                          Complete Registration
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </form>
              );
            }}
          </Formik>
        </Box>
      )}
    </>
  );
};

export default XACRegisterForm;
